export interface TimezoneOption {
  value: string;
  label: string;
}

/** The browser's IANA zone, or UTC on an engine that cannot resolve one. */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Whether this engine can actually format a date in `zone`.
 *
 * Constructing a formatter is the only honest test: `Intl` is the thing that
 * will reject the value later, so asking it directly beats matching the id
 * against a list that is itself engine- and ICU-version-specific.
 */
function isUsableZone(zone: string): boolean {
  try {
    Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * A zone the rest of the app may hand to `Intl` without a guard of its own.
 *
 * Zones reach this page from the backend, where `ZoneId.of` accepts spellings
 * `Intl` rejects outright — `UT`, `Z`, `GMT+05:00` — and nothing upstream stops
 * an AE from pinning one. Every such value is a RangeError the moment it meets
 * `Intl.DateTimeFormat`: thrown inside the load effect it leaves the lead on a
 * permanent spinner, thrown during render it blanks the page. This is a public,
 * lead-facing page, so a zone we cannot use degrades to one we can.
 */
export function safeZone(
  candidate: string | null | undefined,
  fallback: string
): string {
  if (candidate && isUsableZone(candidate)) return candidate;
  if (fallback && isUsableZone(fallback)) return fallback;
  return "UTC";
}

/**
 * The IANA zone ids this engine knows about.
 *
 * `Intl.supportedValuesOf` is ES2022 and is simply absent on iOS 15.0–15.3 and
 * a fair number of older WebViews. Called bare at module scope it throws while
 * the module is still being evaluated, so the whole booking chunk never
 * finishes evaluating and the lead gets a blank page instead of a select with
 * fewer entries in it. Call it optionally, inside a try, and fall back to a
 * list that at least contains the zone the lead is actually in.
 */
const ZONE_IDS: string[] = ((): string[] => {
  try {
    const list = Intl.supportedValuesOf?.("timeZone");
    if (list && list.length > 0) return list;
  } catch {
    // fall through to the resolved zone
  }
  // Deduplicated: on an engine with no zone data the resolved zone IS "UTC",
  // and a repeated <option> value would hand React two children with one key.
  return Array.from(new Set([getUserTimezone(), "UTC"]));
})();

export const TIMEZONES: TimezoneOption[] = ZONE_IDS.map((tz) => ({
  value: tz,
  label: tz,
}));

/**
 * Today's calendar date in `timezone`, as the YYYY-MM-DD the slots endpoint
 * expects.
 *
 * Not `new Date().toISOString()`: that is the date in UTC, which is not the
 * date the lead is looking at. A lead in Asia/Calcutta just past midnight is on
 * the 12th while UTC is still on the 11th, so the page would open on yesterday.
 * Assembled from parts rather than a locale that happens to print ISO order, so
 * the format does not depend on the runtime's ICU data.
 */
export function todayInZone(timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const part = (type: string): string =>
    parts.find((p) => p.type === type)?.value ?? "";

  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function formatTimeInZone(isoString: string, timezone: string): string {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  }).format(d);
}

export function formatDateInZone(isoString: string, timezone: string): string {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  }).format(d);
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
